import { copyOnClipboard, openLastUpdatesWindow, checkForUpdates } from './utils';
import Form from './forms';
import DateUtils from './utils/DateUtils';
import FormUtils from './utils/FormUtils';
import StringUtils from './utils/StringUtils';
import NavigationUtils from './utils/NavigationUtils';
import MerideVideo from './utils/MerideVideo';
import { CrudAction, CopyAction } from './enums';
import Switchery from 'switchery';
import 'summernote/src/lang/summernote-it-IT';
import './vcdn-processing';
import { initPeriodDatepickerFilters } from './components/PeriodDatepickerFilter';
import { initTableActionsDropdowns } from './components/TableActionsDropdown';
import { initTranslationToolbarBridgeChild } from './translation-toolbar-bridge';
import { initMaintenanceWatcher } from './utils/MaintenanceWatcher';
import { initCsrfTokenRefresh } from './utils/CsrfToken';
import ClipboardHistory from './core/ClipboardHistory';
import ResourceUtils from './utils/ResourceUtils';
import { beginSourceMediaDrag, cleanAgencyMediaUrl, setSourceMediaDragging } from './utils/agencyMediaTransfer';
import { normalizeAttributionTags, normalizeSourceKeywords } from './utils/BlockAttribution';
import { initMediaExplorer } from './utils/mediaExplorer';
import { createSourcesPickerBus } from './utils/SourcesPickerBus';
import { clearSourcesPickerPack } from './utils/sourcesPickerStorage';
import OffcanvasDetail from './offcanvas';

/**
 * @param {Element|null|undefined} root
 * @param {string} attribute
 * @returns {unknown[]}
 */
function parseRootJsonArray(root, attribute) {
	if (!root) {
		return [];
	}

	try {
		const parsed = JSON.parse(root.getAttribute(attribute) || '[]');

		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}
FormUtils.patchSwitcheryDisabledHandling(Switchery);

(function initDetachedSourcesPickerTab() {
	if (typeof window === 'undefined') {
		return;
	}

	const params = new URLSearchParams(window.location.search);
	const isDetachedSourcesPicker = params.get('detached') === '1';
	const pickerSessionId = params.get('picker_session');

	if (!isDetachedSourcesPicker || !pickerSessionId) {
		return;
	}

	document.documentElement.classList.add('sources-picker-detached');

	const detachedBus = createSourcesPickerBus({
		sessionId: pickerSessionId,
		role: 'detached',
	});

	detachedBus.onMessage((message) => {
		if (message.kind === 'HOST_CLOSED') {
			document.documentElement.classList.add('sources-picker-host-gone');
		}
	});

	detachedBus.start();
	window.__naxosSourcesPickerBus = detachedBus;
	window.__naxosSourcesPickerSessionId = pickerSessionId;

	window.addEventListener('pagehide', () => {
		try {
			detachedBus.send('DETACHED_CLOSED');
		} catch {
			// Ignore.
		}
		clearSourcesPickerPack(pickerSessionId);
		detachedBus.destroy();
		window.__naxosSourcesPickerBus = null;
		window.__naxosSourcesPickerSessionId = null;
	});
})();

function resolveSourceUsageFromButton($button) {
	const identifier = $button.attr('data-id');
	let source = $button.attr('data-entity');

	if (!source) {
		source = $button.closest('[data-entity]').attr('data-entity');
	}

	if (!source || !identifier) {
		return null;
	}

	return {
		source,
		identifier: String(identifier),
	};
}

function resolveSourceUsageFromElement(element) {
	if (!element) {
		return null;
	}

	const host =
		element.closest?.('[data-source][data-source-id]') ||
		element.closest?.('[data-entity][data-id]') ||
		element.closest?.('[data-id]');

	if (!host) {
		return null;
	}

	const source =
		host.getAttribute('data-source') ||
		host.getAttribute('data-entity') ||
		host.closest?.('[data-entity]')?.getAttribute('data-entity');
	const identifier =
		host.getAttribute('data-source-id') ||
		host.getAttribute('data-id') ||
		host.getAttribute('data-agency-id');

	if (!source || !identifier) {
		return null;
	}

	return {
		source,
		identifier: String(identifier),
	};
}

function withSourceUsagePayload(payload, $button) {
	const usage = resolveSourceUsageFromButton($button);

	if (usage) {
		payload.source = usage.source;
		payload.identifier = usage.identifier;
	}

	return payload;
}

document.addEventListener(
	'dragstart',
	(e) => {
		if (!e.dataTransfer) {
			return;
		}

		const { videoUrl, posterUrl } = ResourceUtils.resolveVideoDragUrlsFromElement(e.target);
		if (!videoUrl || ResourceUtils.isAppPageUrl(videoUrl)) {
			return;
		}

		const usage = resolveSourceUsageFromElement(e.target);
		const dragTargetWindow = window.parent !== window ? window.parent : window;
		ResourceUtils.storeNaxosDragPayload(dragTargetWindow, videoUrl, posterUrl, usage);

		if (window.parent !== window) {
			window.parent.postMessage(
				{
					action: 'naxos-drag-video',
					videoUrl,
					posterUrl,
					source: usage?.source ?? null,
					identifier: usage?.identifier ?? null,
				},
				window.location.origin,
			);
		}

		const videoLink = e.target.closest?.(
			'a.popup-preview-video, a.popup-preview-image[video-href], a.popup-preview-image[data-video-url]',
		);
		const dragHtml = videoLink?.outerHTML || `<span data-naxos-video-url="${videoUrl}"></span>`;

		e.dataTransfer.setData('text/plain', videoUrl);
		e.dataTransfer.setData('text/uri-list', videoUrl);
		e.dataTransfer.setData('application/x-naxos-video-url', videoUrl);
		e.dataTransfer.setData('text/html', dragHtml);
		e.dataTransfer.effectAllowed = 'copy';
	},
	true,
);

if (window.parent === window) {
	window.addEventListener('message', (event) => {
		if (event.origin !== window.location.origin) {
			return;
		}

		const data = event.data;
		if (data?.action !== 'naxos-drag-video' || !data.videoUrl) {
			return;
		}

		ResourceUtils.storeNaxosDragPayload(window, data.videoUrl, data.posterUrl ?? null, {
			source: data.source ?? null,
			identifier: data.identifier ?? null,
		});
	});
}

toastr.options = {
	positionClass: 'toast-bottom-left',
};

window.Utils = {
	LoaderShow: NavigationUtils.LoaderShow,
	LoaderHide: NavigationUtils.LoaderHide,
	createAndOpenPanel: NavigationUtils.createAndOpenPanel,
	withBackUrl: NavigationUtils.withBackUrl,
	openLastUpdatesWindow: openLastUpdatesWindow,
	copyOnClipboard: copyOnClipboard,
};

$.extend(true, $.magnificPopup.defaults, {
	tClose: 'Chiudi (Esc)', // Alt text on close button
	tLoading: 'Caricamento in corso...', // Text that is displayed during loading. Can contain %curr% and %total% keys
	gallery: {
		tPrev: 'Precedente (Freccia sinistra)', // Alt text on left arrow
		tNext: 'Successivo (Freccia destra)', // Alt text on right arrow
		tCounter: '%curr% di %total%', // Markup for "1 of 7" counter
	},
	image: {
		tError: 'Immagine non caricata.', // Error message when image could not be loaded
	},
	ajax: {
		tError: 'Contenuto non caricato.', // Error message when ajax request failed
	},
});

window.addEventListener('unload', () => {
	NavigationUtils.LoaderShow();
	// if (navigator.sendBeacon) {
	// 	const url = window.appconfig.routes.yourEndpoint || '/api/your-endpoint';
	// 	navigator.sendBeacon(url);
	// } else {
	// 	// Fallback for older browsers
	// 	const xhr = new XMLHttpRequest();
	// 	xhr.open('POST', url, false); // synchronous request
	// 	xhr.setRequestHeader('Content-Type', 'application/json');
	// 	xhr.send();
	// }
});

$(function () {
	FormUtils.bindSwitcheryVisibilityRefresh();

	// Expose a refresh hook so an embedder (the translation split-view parent) can re-position this
	// page's switches once it makes the iframe visible — Switchery measures the knob at init and a
	// hidden iframe reports 0px, leaving checked switches misaligned until something re-measures them.
	window.refreshSwitcheryPositions = (scope) => FormUtils.scheduleSwitcheryPositionRefresh(scope);

	// inizializzazione campi switch
	const elems = document.querySelectorAll('input[data-plugin="switchery"], input[data-switchery]');
	for (let i = 0; i < elems.length; i++) {
		const elem = elems[i];
		const size = elem.getAttribute('data-size') || 'default';
		new Switchery(elem, {
			size: size,
			color: 'var(--primary)',
			secondaryColor: 'var(--gray)',
		});
	}

	FormUtils.scheduleSwitcheryPositionRefresh();
	FormUtils.observeSwitcheryVisibility();

	initTranslationToolbarBridgeChild();

	// Cronologia degli appunti copiati (dropdown in navbar)
	window.ClipboardHistory = new ClipboardHistory();
	window.ClipboardHistory.init();

	FormUtils.bindInvalidFieldTooltipHandlers();

	window.form = $('#pageForm').length ? new Form() : null;

	$('#openMenuForMobile').on('click', function () {
		if ($('body').hasClass('site-menubar-hide')) {
			$('body').removeClass('site-menubar-hide').addClass('site-menubar-unfold');
		} else {
			$('body').removeClass('site-menubar-unfold').addClass('site-menubar-hide');
		}
	});

	$('[rel=tooltip]').tooltip({ boundary: 'window' });
	$('[rel=tooltip-offset]').tooltip({ boundary: 'window', offset: '0, 13' });

	// GESTIONE COMUNE CAMPI FORMS DI TIPO DATA
	// flatpickr dispatches an untrusted `change`, so flag the interaction and re-dispatch to run dirty tracking.
	const notifyDateFieldChanged = (field) => {
		if (window.form) {
			window.form._user_interacted = true;
		}
		$(field).trigger('change');
	};
	$('[data-no-date]').on('click', function () {
		const field = document.getElementById(this.getAttribute('data-field-identifier'));
		DateUtils.getFlatPickr(field).clear();
		notifyDateFieldChanged(field);
	});
	$('[data-current-date]').on('click', function () {
		const field = document.getElementById(this.getAttribute('data-field-identifier'));
		DateUtils.timeSync(field);
		const clock = $(this).find('i')[0];
		DateUtils.replaceClockIcon(clock, field.value.split(' al ')[0]);
		notifyDateFieldChanged(field);
	});
	$('[data-inputmask]').each((_idx, el) => {
		const fp = DateUtils.getFlatPickr(el);
		// Calendar selection only flows through flatpickr; scoped to #pageForm to skip filter datepickers.
		if (el.closest('#pageForm')) {
			fp.config.onChange.push(() => notifyDateFieldChanged(el));
		}
	});

	initPeriodDatepickerFilters();
	initTableActionsDropdowns();

	// Keep text selection alive when clicking copy in a text panel: otherwise the
	// button steals focus and the selection is gone before copyOnClipboard runs.
	$(document).on(
		'mousedown',
		'.editor-actbar [data-copy], .offcanvas-detail-text [data-copy], .offcanvas-translation-container [data-copy]',
		function (e) {
			e.preventDefault();
		},
	);

	// gestione campi copia valore
	$(document).on('click', '[data-copy]', function (e) {
		e.preventDefault();

		// Copy selection when present in the related text box, otherwise full data-copy
		copyOnClipboard(this);

		// In iframe/popup (es. selezione video VCDN): non fare stopPropagation,
		// così il handler successivo può inviare postMessage set-url al parent
		if (window.self !== window.top) {
			return;
		}
		e.stopPropagation();

		return false;
	});

	$(document).on('click', '[data-download]', function (e) {
		e.preventDefault();
		e.stopPropagation();
		const url = $(e.currentTarget).attr('data-download');
		if (!url.length) return false;

		window.form?.clearConfirmPageLeave();
		location.href = window.appconfig.routes.files.download + '?url=' + encodeURIComponent(url);
		if (window.needsConfirm) {
			window.form?.setConfirmPageLeave();
		}

		return false;
	});

	// initializzazione bottone dettaglio tabelle

	// Controllo chiusura popup
	const urlParams = new URLSearchParams(window.location.search);
	if (urlParams.get('popup') === 'true' || window.self !== window.top) {
		window.addEventListener('message', function (event) {
			if (event.data === 'requestClose') {
				if (window.needsConfirm) {
					window.parent.postMessage('preventClose', '*');
					console.info('postMessage: prevent close');
				} else {
					const unlock = $('#unlockurl');
					if (unlock.length) {
						NavigationUtils.LoaderShow();
						if (window.pollingInterval) {
							clearInterval(window.pollingInterval);
							pollingInterval = null;
							if (window.pollingCall) {
								window.pollingCall.abort("Annullato dall'utente");
								window.pollingCall = null;
							}
						}
						$.ajax(StringUtils.localizeUrl(unlock.val())).then(() => {
							window.parent.postMessage('allowClose', '*');
							console.info('postMessage: allow close');
						});
					} else {
						window.parent.postMessage('allowClose', '*');
						console.info('postMessage: allow close');
					}
				}
			} else if (event.data === 'pong') {
				window.dispatchEvent(new Event('handshakeDone'));
			} else if (event.data === 'changelogUpdated') {
				console.info('postMessage: changelog updated');
				checkForUpdates();
			} else if (event.data?.action === 'setNeedsConfirm') {
				// requestClose reads window.needsConfirm: reset it here, not only the form flag,
				// otherwise the parent's "OK" is ignored and the confirm comes back in a loop.
				window.needsConfirm = event.data.value;

				if (window.form) {
					window.form.needsConfirm = event.data.value;
				}

				if (!event.data.value) {
					window.pageEditGuard?.clearConfirmPageLeave();
				}
			}
		});

		const send_popup_handshake_ping = () => {
			window.parent.postMessage('ping', '*');
		};

		if (typeof $ !== 'undefined') {
			$(() => {
				setTimeout(send_popup_handshake_ping, 50);
			});
		} else {
			setTimeout(send_popup_handshake_ping, 50);
		}

		console.info('Inizializzato protocollo risposta a requestClose');

		const pageTitleMeta = document.querySelector('meta[name="page-title"]');
		if (pageTitleMeta && pageTitleMeta.content) {
			window.parent.postMessage({ action: 'set-title', title: pageTitleMeta.content }, '*');
		}
	}

	if (window.self !== window.top) {
		const url_params = new URLSearchParams(window.location.search);
		let is_offcanvas_embedded = url_params.get('offcanvas') === 'true';

		if (!is_offcanvas_embedded) {
			try {
				is_offcanvas_embedded =
					window.frameElement?.classList?.contains('offcanvas-iframe') === true
					|| window.parent?.document?.querySelector('#offcanvas-content')?.contains(window.frameElement) === true;
			} catch (_error) {
				is_offcanvas_embedded = false;
			}
		}

		// inietta il flag popup nell'url se è un redirect interno ad un iframe
		$('a[href]:not(.popup-preview-image):not(.popup-preview-gallery-image):not(.popup-preview-video):not(.media-explorer-slide):not(.back-link):not(#close)').each((_idx, el) => {
			const href = el.getAttribute('href');
			if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
				return;
			}

			const separator = href.includes('?') ? '&' : '?';
			let next_href = href;

			if (!href.includes('popup=true')) {
				next_href += `${separator}popup=true`;
			}

			if (is_offcanvas_embedded && !next_href.includes('offcanvas=true')) {
				next_href += `${next_href.includes('?') ? '&' : '?'}offcanvas=true`;
			}

			if (next_href !== href) {
				el.setAttribute('href', next_href);
			}
		});

		const action = urlParams.get('action');
		if (action) {
			$(`.action-button:not([data-copy]):not([data-action="children"]):not(.stamper)`).removeClass('inline').removeClass('d-flex').hide();
			// $('.actions .btn-group:has(.dropdown-menu):not(:has(.dopdown-item:visible))').hide();
		}

		$(document).on('click', '[data-copy]', function () {
			const $button = $(this);
			const buttonAction = $button.attr('data-action');
			const thumbUrl = $button.attr('data-copy-thumb') || null;

			// Se c'è un parametro 'action' nell'URL che specifica quale tipo di contenuto stiamo inserendo
			if (action) {
				// Se l'action è VIDEO e clicco sull'immagine, devo comunque inserire il video
				if (action === CopyAction.VIDEO && buttonAction === CopyAction.IMAGE) {
					// Cerca il pulsante video corrispondente (stesso data-id)
					const $videoButton = $(`[data-copy][data-action="${CopyAction.VIDEO}"][data-id="${$button.attr('data-id')}"]`);
					if ($videoButton.length > 0) {
						// Invia il video come URL principale e l'immagine come secondario
						window.parent.postMessage(
							withSourceUsagePayload(
								{ action: 'set-url', url: $videoButton.attr('data-copy'), buttonAction: CopyAction.VIDEO },
								$videoButton,
							),
							'*',
						);
						window.parent.postMessage({ action: 'set-secondary-url', url: $button.attr('data-copy') }, '*');
					}
					return; // L'immagine è già stata copiata negli appunti dall'handler precedente
				}

				// In tutti gli altri casi con action, conferma solo se il buttonAction corrisponde
				if (action === buttonAction) {
					window.parent.postMessage(
						withSourceUsagePayload(
							{ action: 'set-url', url: $button.attr('data-copy'), thumb: thumbUrl, buttonAction: buttonAction },
							$button,
						),
						'*',
					);
					// $('[data-copy]').each(function () {
					// 	if ($button.attr('data-action') === $(this).attr('data-action') && $button.attr('data-id') === $(this).attr('data-id')) {
					// 		$(this).toggleClass('selected', true);
					// 	} else {
					// 		$(this).toggleClass('selected', false);
					// 	}
					// });

					// Se stiamo confermando un video, invia anche l'immagine come URL secondario
					if (buttonAction === CopyAction.VIDEO) {
						const $siblings = $button.siblings(`[data-action="${CopyAction.IMAGE}"]`);
						if ($siblings.length > 0 && $siblings[0].getAttribute('data-copy') !== '') {
							window.parent.postMessage({ action: 'set-secondary-url', url: $siblings[0].getAttribute('data-copy') }, '*');
						}
					}
				}
			} else {
				// Nessun action specificato: comportamento predefinito (conferma tutto)
				window.parent.postMessage(
					withSourceUsagePayload(
						{ action: 'set-url', url: $button.attr('data-copy'), thumb: thumbUrl, buttonAction: buttonAction },
						$button,
					),
					'*',
				);
				// $('[data-copy]').each(function () {
				// 	if ($button.attr('data-action') === $(this).attr('data-action') && $button.attr('data-id') === $(this).attr('data-id')) {
				// 		$(this).toggleClass('selected', true);
				// 	} else {
				// 		$(this).toggleClass('selected', false);
				// 	}
				// });

				// Se stiamo confermando un video, invia anche l'immagine come URL secondario
				if (buttonAction === CopyAction.VIDEO) {
					const $siblings = $button.siblings(`[data-action="${CopyAction.IMAGE}"]`);
					if ($siblings.length > 0 && $siblings[0].getAttribute('data-copy') !== '') {
						window.parent.postMessage({ action: 'set-secondary-url', url: $siblings[0].getAttribute('data-copy') }, '*');
					}
				}
			}
		});
	}

	// Detached sources picker is a top-level tab: preserve session flags on internal links.
	if (urlParams.get('detached') === '1' && urlParams.get('picker_session')) {
		const picker_session = urlParams.get('picker_session');
		const keep_offcanvas = urlParams.get('offcanvas') === 'true';

		$('a[href]:not(.popup-preview-image):not(.popup-preview-gallery-image):not(.popup-preview-video):not(.media-explorer-slide):not(.back-link):not(#close)').each((_idx, el) => {
			const href = el.getAttribute('href');
			if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
				return;
			}

			try {
				const next = new URL(href, window.location.origin);
				if (next.origin !== window.location.origin) {
					return;
				}

				next.searchParams.set('popup', 'true');
				next.searchParams.set('detached', '1');
				next.searchParams.set('picker_session', picker_session);
				if (keep_offcanvas) {
					next.searchParams.set('offcanvas', 'true');
				}

				el.setAttribute('href', next.pathname + next.search + next.hash);
			} catch {
				// Ignore invalid hrefs.
			}
		});
	}

	window.addEventListener(
		'message',
		function (event) {
			if (event.origin != this.location.origin) {
				return;
			}
			if (event.data && typeof event.data === 'object' && event.data.action === 'open-preview' && event.data.url) {
				NavigationUtils.createAndOpenPanel(event.data.url);
				return;
			}
			switch (event.data) {
				case 'closeup':
					$('#panel #close-popup').trigger('click');
					break;
			}
		},
		false,
	);

	const initImpersonatePair = (roleSelector, usersSelector) => {
		const $role = $(roleSelector);
		const $users = $(usersSelector);
		if (!$users.length) {
			return;
		}

		if ($role.length) {
			FormUtils.initializeSelect2(roleSelector, false, '180px', true, $role.attr('data-placeholder') || 'Ruolo', false, {
				allowClear: true,
				placeholder: $role.attr('data-placeholder') || 'Ruolo',
			});
		}

		FormUtils.initializeSelect2(usersSelector, false, '100%', true, $users.attr('data-placeholder') || 'Impersonifica', false, {
			ajax: {
				url: window.appconfig.routes.journalists.impersonate,
				data: function (params) {
					const role_id = $role.length ? $role.val() : null;
					const query = {
						q: params.term,
						page: params.page || 1,
						selected: $users.val(),
					};
					if (role_id) {
						query.role_id = role_id;
					}
					return query;
				},
			},
		});

		$role.on('change', function () {
			$users.val(null).trigger('change');
			$(`.js-impersonate-btn[data-users-select="${usersSelector}"]`).addClass('disabled');
		});

		$users.on('change', function () {
			const $btn = $(`.js-impersonate-btn[data-users-select="${usersSelector}"]`);
			if ($users.val()) {
				$btn.removeClass('disabled');
			} else {
				$btn.addClass('disabled');
			}
		});
	};

	initImpersonatePair('#impersonate-role', '#impersonate');
	initImpersonatePair('#impersonate-role-mobile', '#impersonate-mobile');

	$(document).on('click', '.js-impersonate-btn:not(.disabled)', function (e) {
		e.preventDefault();
		const users_selector = $(this).data('users-select');
		const user_id = $(users_selector).val();
		if (!user_id) {
			return;
		}
		location.href = `/app/impersonate/take/${user_id}/cms`;
	});

	let userFiltersChanged = false;
	if ($('#filtersForm').length) {
		$('#filtersForm').on('change', function () {
			userFiltersChanged = true;
		});

		window.addEventListener('keyup', function (e) {
			if (e.key === 'Enter' && userFiltersChanged) {
				$('#filtersForm').trigger('submit');
			}
		});

		// Page-scoped language filter: render flags and reload the page with the chosen language.
		if ($('#filtersForm [data-language-filter]').length) {
			const drawLanguageFlag = (data, withLabel) => {
				if (!data.id) {
					return data.text;
				}
				const element = data.element;
				const flag = element?.getAttribute('data-flag') || element?.getAttribute('data-locale') || data.id;
				const code = String(data.id).toUpperCase();
				const img = `<img class="language-filter-flag" src="/images/flags/${flag}.png" alt="${code}" style="height: 14px; width: auto;${withLabel ? ' margin-right: 8px;' : ''}" onerror="this.replaceWith(document.createTextNode('${code}'))" />`;
				return $(`<span class="d-flex align-items-center">${img}${withLabel ? `<span>${data.text}</span>` : ''}</span>`);
			};

			FormUtils.initializeSelect2('#filtersForm [data-language-filter]', false, 'auto', false, null, false, {
				allowClear: false,
				minimumResultsForSearch: Infinity,
				templateSelection: (data) => drawLanguageFlag(data, false),
				templateResult: (data) => drawLanguageFlag(data, true),
			});

			$('#filtersForm').on('change', '[data-language-filter]', function () {
				$('#filtersForm').trigger('submit');
			});
		}
	}

	// New elements are always created starting from the default language. When the user opens
	// the create page while viewing a non-default language filter, the language is silently
	// reset to the default one: show an info box so they know what happened.
	if (window.languageResetNotice) {
		bootbox.alert(window.languageResetNotice);
	}

	$('button[data-current-date]').each(function () {
		const field = $('#' + $(this).attr('data-field-identifier'));
		const clock = $(this).find('i')[0];
		DateUtils.replaceClockIcon(clock, field.val().split(' al ')[0]);
		// splitto nel caso dei date range di datepickr
		field.on('change', () => DateUtils.replaceClockIcon(clock, field.val().split(' al ')[0]));
	});

	$('.popup-preview-image').not('.media-explorer-gallery .media-explorer-slide').magnificPopup({
		type: 'image',
		closeOnContentClick: true,
		closeBtnInside: false,
		mainClass: 'mfp-with-zoom mfp-img-mobile',
		image: {
			verticalFit: true,
			titleSrc: function (item) {
				return item.el.attr('title') ?? '';
			},
		},
		zoom: {
			enabled: true,
		},
		gallery: {
			enabled: true,
		},
	});

	$('.popup-preview-gallery').each(function () {
		$(this).magnificPopup({
			delegate: '.popup-preview-gallery-image',
			type: 'image',
			closeOnContentClick: true,
			closeBtnInside: false,
			mainClass: 'mfp-with-zoom mfp-img-mobile',
			image: {
				verticalFit: true,
				titleSrc: function (item) {
					return item.el.attr('title') ?? '';
				},
			},
			zoom: {
				enabled: true,
			},
			gallery: {
				enabled: true,
			},
		});
	});

	$('.popup-preview-iframe').magnificPopup({
		type: 'inline',
		closeOnContentClick: true,
		closeBtnInside: false,
		mainClass: 'mfp-with-zoom mfp-img-mobile',
		zoom: {
			enabled: true,
		},
		gallery: {
			enabled: true,
		},
	});

	const isResourcePickerIframe = window.self !== window.top || urlParams.get('popup') === 'true';
	if (!isResourcePickerIframe) {
		$('.popup-preview-video').magnificPopup({
			type: 'iframe',
			closeOnContentClick: false,
			closeBtnInside: false,
			mainClass: 'mfp-with-zoom mfp-img-mobile',
			zoom: {
				enabled: true,
			},
			gallery: {
				enabled: false,
			},
			callbacks: {
				elementParse: function (item) {
					const videoType = item.el.attr('data-video-type');
					if (videoType === 'video') {
						item.type = 'inline';
						const videoUrl = item.el.attr('href');
						item.src = `<div class="mfp-video-container" style="max-width: 900px; margin: 0 auto; background: #000;">
						<video controls autoplay style="width: 100%; max-height: 80vh;">
							<source src="${videoUrl}" type="video/mp4">
							Il tuo browser non supporta il tag video.
						</video>
					</div>`;
					} else if (videoType === 'meride') {
						item.type = 'inline';
						const customer = item.el.attr('data-meride-customer') || window.merideCustomerId;
						const code = item.el.attr('data-meride-code');
						const { html, containerId } = MerideVideo.createPopupContainer(customer, code);
						item.src = html;
						MerideVideo.initPopupPlayer(containerId, customer, code);
					}
				},
				close: function () {
					const video = document.querySelector('.mfp-video-container video');
					if (video) {
						video.pause();
						video.src = '';
					}
					MerideVideo.cleanupPopupPlayers();
				},
			},
		});
	}

	initMediaExplorer(document);

	$(document).on('click', '.timeline-item a, .related-material-item a', function () {
		const li = $(this).closest('li');
		const id = li.attr('data-id');
		const entity = li.attr('data-entity');
		if (!id) return;

		if ($('[data-toggle="offcanvas"][data-id="' + id + '"]').length) {
			console.info('found element, triggering show details');
			$('[data-toggle="offcanvas"][data-id="' + id + '"]').trigger('click');
			$('.offcanvas-body').animate({ scrollTop: 0 });
		} else {
			console.info('no element found, redirecting to correct page');
			const $btn = $('<button>').attr('data-id', id).attr('data-entity', entity).attr('data-toggle', 'offcanvas').attr('type', 'button');
			document.dispatchEvent(new CustomEvent('openOffcanvas', { detail: { data: { entity, id, action: 'children' }, btn: $btn } }));
		}
	});

	$(document).on(
		'dragstart',
		[
			'.offcanvas-images[data-agency-media] .popup-preview-image',
			'.offcanvas-images[data-agency-media] .media-explorer-slide[data-media-type="image"]',
			// Collaboratori/Agenzie picker iframe (copy-image-url + card_essential mosaic)
			'[data-entity][data-id] .popup-preview-image',
			'[data-entity][data-id] .media-explorer-slide[data-media-type="image"]',
			'.mfp-media-explorer-image[data-source-id]',
			'.mfp-media-explorer-thumb[data-media-type="image"][data-source-id]',
			// Midjourney AI listing (popup or offcanvas iframe)
			'.midjourney-image[data-source] .popup-preview-image',
			'.midjourney-image[data-source] img',
		].join(', '),
		function (e) {
			if (!e.originalEvent?.dataTransfer) {
				return;
			}

			// Video source cards (VCDN): the video dragstart handler owns this drag, an image pack would win the drop.
			if (this.closest('[data-naxos-video-url]')) {
				return;
			}

			const root = this.closest('.offcanvas-images[data-agency-media]');
			const card = this.closest('[data-entity][data-id]');
			// Listing cards/rows carry keywords; offcanvas detail uses .offcanvas-images.
			const metaRoot = root || card;
			const from_element = resolveSourceUsageFromElement(this);
			const source =
				this.getAttribute('data-source') ||
				metaRoot?.getAttribute('data-source') ||
				from_element?.source ||
				card?.getAttribute('data-entity') ||
				'agency';
			const identifier =
				this.getAttribute('data-source-id') ||
				metaRoot?.getAttribute('data-source-id') ||
				metaRoot?.getAttribute('data-agency-id') ||
				from_element?.identifier ||
				card?.getAttribute('data-id');
			const signature =
				this.getAttribute('data-source-signature') ||
				metaRoot?.getAttribute('data-source-signature') ||
				null;
			const keywords = normalizeSourceKeywords(parseRootJsonArray(metaRoot, 'data-source-keywords'));
			const tagIds = normalizeAttributionTags(parseRootJsonArray(metaRoot, 'data-source-tag-ids'));

			const copy_btn = card?.querySelector?.('[data-action="copy-image-url"][data-copy]');
			const href =
				this.getAttribute('data-media-url') ||
				this.getAttribute('href') ||
				copy_btn?.getAttribute('data-copy') ||
				(this.tagName === 'IMG' ? this.getAttribute('src') : null);

			let blocks = [];
			if (href) {
				const cleanedUrl = cleanAgencyMediaUrl(href);
				let author = '';
				let caption = this.getAttribute('title') || this.getAttribute('alt') || '';

				try {
					const packBlocks = JSON.parse(root?.getAttribute('data-agency-media') || '[]');
					const match = Array.isArray(packBlocks)
						? packBlocks.find((block) => {
								if (block?.type !== 'image') {
									return false;
								}
								const blockUrl = block.data?.file?.url;
								return typeof blockUrl === 'string' && (blockUrl === cleanedUrl || blockUrl.includes(cleanedUrl) || cleanedUrl.includes(blockUrl));
							})
						: null;
					if (match?.data?.file?.author) {
						author = String(match.data.file.author);
					}
					if (!caption && match?.data?.file?.caption) {
						caption = String(match.data.file.caption);
					}
					if (!caption && match?.data?.caption) {
						caption = String(match.data.caption);
					}
				} catch {
					// Ignore malformed pack JSON.
				}

				blocks = [
					{
						type: 'image',
						data: {
							file: { url: cleanedUrl, author: author || null },
							caption,
							author,
							source: {
								name: source,
								identifier: identifier != null ? String(identifier) : null,
								signature,
								url: cleanedUrl,
								file: null,
								keywords,
							},
							attribution: {
								journalist_id: null,
								tags: tagIds,
							},
						},
					},
				];
			} else if (root) {
				try {
					blocks = JSON.parse(root.getAttribute('data-agency-media') || '[]');
				} catch {
					blocks = [];
				}
			}

			if (!Array.isArray(blocks) || blocks.length === 0) {
				return;
			}

			if (signature || source || keywords || tagIds.length > 0) {
				blocks = blocks.map((block) => {
					if (!block || typeof block !== 'object') {
						return block;
					}

					const data = block.data && typeof block.data === 'object' ? { ...block.data } : {};
					const existingSource = data.source && typeof data.source === 'object' ? data.source : {};
					const resolvedSignature = existingSource.signature || signature;
					data.source = {
						name: existingSource.name || source,
						identifier: existingSource.identifier || (identifier != null ? String(identifier) : null),
						signature: resolvedSignature,
						url: existingSource.url || data.file?.url || data.source_url || null,
						file: existingSource.file ?? null,
						keywords: normalizeSourceKeywords(existingSource.keywords) || keywords,
					};

					const existingAttribution =
						data.attribution && typeof data.attribution === 'object' ? data.attribution : {};
					const existingTags = normalizeAttributionTags(existingAttribution.tags);
					data.attribution = {
						journalist_id:
							existingAttribution.journalist_id != null && existingAttribution.journalist_id !== ''
								? Number(existingAttribution.journalist_id)
								: null,
						tags: existingTags.length > 0 ? existingTags : tagIds,
					};

					if (!data.author && data.file?.author) {
						data.author = data.file.author;
					}

					if (!data.author && String(source).toLowerCase() === 'ai') {
						data.author = resolvedSignature || 'Midjourney';
					}

					return { ...block, data };
				});
			}

			beginSourceMediaDrag(
				{
					source,
					identifier,
					signature,
					keywords,
					tag_ids: tagIds,
					blocks,
				},
				e.originalEvent.dataTransfer,
			);
		},
	);

	$(document).on('dragend', () => {
		// Keep flag briefly so cross-iframe drop handlers still see an active drag.
		window.setTimeout(() => setSourceMediaDragging(false), 2000);

		const bus = window.__naxosSourcesPickerBus;
		if (bus) {
			bus.send('DRAG_END');
		}
	});

	window.addEventListener('message', (event) => {
		if (event.origin !== window.location.origin) {
			return;
		}

		const data = event.data;
		if (data?.action !== 'naxos-source-media-drag' || !data.source || !data.identifier) {
			return;
		}

		window.__pendingSourceUsage = {
			source: String(data.source),
			identifier: String(data.identifier),
		};
		window.__pendingAgencyMediaPack = {
			source: String(data.source),
			identifier: String(data.identifier),
			agency_id: String(data.identifier),
			signature: data.signature != null && String(data.signature).trim() !== '' ? String(data.signature).trim() : null,
			blocks: Array.isArray(data.blocks) ? data.blocks : [],
		};
	});

	// Previeni il comportamento di default del browser
	$(document).on('dragover drop', function (e) {
		e.preventDefault();
	});

	checkForUpdates();

	const $filtersPanel = $('.filters-panel--popup');
	let redrawFiltersFn = null;

	const isFiltersPanelCollapsed = () => $filtersPanel.length > 0 && $filtersPanel.hasClass('filters-collapsed');

	const runRedrawFilters = () => {
		if (isFiltersPanelCollapsed() || typeof redrawFiltersFn !== 'function') {
			return;
		}

		redrawFiltersFn();
	};

	if ($filtersPanel.length) {
		const syncCollapsedState = () => {
			const isCollapsed = $filtersPanel.hasClass('filters-collapsed');
			document.body.classList.toggle('filters-collapsed', isCollapsed);
		};

		syncCollapsedState();

		$(document).on('click', '.filters-panel-toggle', function () {
			const $panel = $(this).closest('.filters-panel--popup');
			const isCollapsed = $panel.toggleClass('filters-collapsed').hasClass('filters-collapsed');

			document.body.classList.toggle('filters-collapsed', isCollapsed);
			$(this).attr('aria-expanded', isCollapsed ? 'false' : 'true');

			if (!isCollapsed) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						runRedrawFilters();
					});
				});
			}
		});
	}

	const filters = $('#filtersForm');
	if (filters.length) {
		let resizeTimer = null;
		const dynamicContainer = filters.find('.dynamic-filters-container');

		if (dynamicContainer.length) {
			const filtersItems = filters.find('.d-flex.flex-column');

			if (filtersItems.length > 0) {
				filtersItems.each((idx, el) => $(el).css('order', idx));

				const hiddenFieldsContainer = $('#hidden-fields-container');
				const hiddenFieldsDropdownList = hiddenFieldsContainer.find('.dropdown ul');
				const allFiltersContainer = filters.find('.filter-fields-container');

				const defaultButtonSize = 40;
				const defaultFilterSize = 155;
				let defaultQFilterSize = allFiltersContainer.find('.q-filter').get(0)?.style.minWidth || 200;
				defaultQFilterSize = parseInt(defaultQFilterSize);

				filtersItems.each(function (_idx, el) {
					if (el.classList.contains('hidden-filter')) {
						$(el).detach();
						hiddenFieldsDropdownList.append(el);
					}
				});

				function redrawFilters() {
					const allFiltersContainerWidth = Math.ceil(allFiltersContainer.get(0)?.getBoundingClientRect().width || 0);
					if (allFiltersContainerWidth <= 0) {
						return;
					}

					const getOrder = (element) => parseInt(element.style.order || 0, 10);
					const isManageableFilter = (element) => !element.classList.contains('hidden-filter');

					const currentlyVisible = dynamicContainer.children().get().filter(isManageableFilter);
					const inDropdown = hiddenFieldsDropdownList.children().get().filter(isManageableFilter);
					const manageableFilters = [...currentlyVisible, ...inDropdown];

					const referenceFilter = currentlyVisible[0] ?? inDropdown[0];
					const singleFilterSize = Math.ceil(referenceFilter?.getBoundingClientRect().width || defaultFilterSize);
					const availableContainerSize = allFiltersContainerWidth - defaultQFilterSize - defaultButtonSize;
					let visibleSlots =
						window.innerWidth > 768
							? Math.min(Math.floor(availableContainerSize / singleFilterSize), manageableFilters.length)
							: 0;
					visibleSlots = Math.max(0, visibleSlots);

					// Fixed filters stay visible while there is room; when space runs out, non-fixed hide first.
					const rankedFilters = [...manageableFilters].sort((a, b) => {
						const aIsFixed = a.classList.contains('fixed-filter');
						const bIsFixed = b.classList.contains('fixed-filter');
						if (aIsFixed !== bIsFixed) {
							return aIsFixed ? -1 : 1;
						}

						return getOrder(a) - getOrder(b);
					});

					const filtersToShow = rankedFilters.slice(0, visibleSlots).sort((a, b) => getOrder(a) - getOrder(b));
					const filtersToHide = rankedFilters.slice(visibleSlots);

					manageableFilters.forEach((element) => {
						$(element).detach();
					});

					filtersToShow.forEach((element) => {
						dynamicContainer.append(element);
					});

					filtersToHide.forEach((element) => {
						hiddenFieldsDropdownList.append(element);
					});

					if (hiddenFieldsDropdownList.children().length > 0) {
						if (hiddenFieldsContainer.hasClass('d-none')) {
							hiddenFieldsContainer.removeClass('d-none').addClass('d-flex');
						}
					} else {
						if (hiddenFieldsContainer.hasClass('d-flex')) {
							hiddenFieldsContainer.removeClass('d-flex').addClass('d-none');
						}
					}
				}

				redrawFiltersFn = redrawFilters;
				runRedrawFilters();

				window.addEventListener('resize', () => {
					if (resizeTimer) {
						clearTimeout(resizeTimer);
					}
					resizeTimer = setTimeout(() => {
						runRedrawFilters();
					}, 200);
				});
			}
		}

		$(document).on(
			'click mousedown',
			'#hidden-fields-container .dropdown-menu .select2-container, #hidden-fields-container .dropdown-menu .select2-search__field, #hidden-fields-container .dropdown-menu .select2-results__option',
			function (e) {
				e.stopPropagation();
			},
		);

		// Impedisce che l'interazione con i filtri Select2 chiuda la dropdown dei filtri nascosti
		$('#hidden-fields-container .dropdown').on('hide.bs.dropdown', function (e) {
			const clickEvent = e.clickEvent || e.relatedTarget?.clickEvent;
			if (!clickEvent) {
				// chiusure programmatiche o da tastiera le lasciamo passare
				return;
			}

			const $target = $(clickEvent.target);

			// Se il click arriva:
			// - da dentro il menu dropdown (label, select, ecc.)
			// - oppure da un elemento Select2 (contenitore o dropdown delle opzioni)
			// allora non chiudiamo la dropdown dei filtri nascosti.
			if (
				$target.closest('#hidden-fields-container .dropdown-menu').length ||
				$target.closest('.select2-container').length ||
				$target.closest('.select2-dropdown').length
			) {
				e.preventDefault();
			}
		});
	}

	initMaintenanceWatcher();
	initCsrfTokenRefresh();
});

$(function () {
	if (document.getElementById('offcanvasVersions')) {
		new OffcanvasDetail();
	}
});
